"use client";

import axios from "axios";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
} from "@stocks/ui";
import {
  ITEM_CATEGORY_VALUES,
  type ItemImageSearchResult,
} from "@stocks/shared";

import { useItemDraftStore } from "@/shared/libs/zustand/stores/itemDraft.store";
import {
  EMPTY_ITEM_FORM,
  ITEM_CATEGORY_LABELS,
  ItemFormFields,
  toCreateItemRequest,
  type ItemFormValues,
} from "@/entities/item";
import { useCreateItem } from "@/features/item/create-item";
import { ImagePicker, useImagePicker } from "@/features/item/image-picker";
import {
  ItemRecommendations,
  useSearchItemsByImage,
} from "@/features/item/search-by-image";
import { useUploadItemImage } from "@/features/item/upload-image";
import { useUpdateItem } from "@/features/item/update-item";

type Step = "search" | "form";

/**
 * 품목 등록/입고 진입 화면.
 * 1) 사진(+분류/규격) → 기존 품목 이미지 비교 추천 → 입고 또는 신규 등록
 * 2) 신규 등록: 사진/분류/규격을 유지한 채 품목 정보 입력 → 저장 후 사진 업로드
 */
export const ItemRegister = () => {
  const router = useRouter();
  const draft = useItemDraftStore();
  const picker = useImagePicker(draft.files);
  const form = useForm<ItemFormValues>({
    defaultValues: {
      ...EMPTY_ITEM_FORM,
      category: draft.category,
      spec: draft.spec,
    },
  });
  const [step, setStep] = useState<Step>("search");
  const [createdId, setCreatedId] = useState<string | null>(null);

  const search = useSearchItemsByImage();
  const createItem = useCreateItem();
  const upload = useUploadItemImage();
  const updateItem = useUpdateItem({ silent: true });
  const uploadingRef = useRef(false);

  const { images, primaryId } = picker;
  const hasFailed = images.some((i) => i.status === "error");
  const isBusy =
    createItem.isPending || uploadingRef.current || upload.isPending;

  const runSearch = () => {
    const { category, spec } = form.getValues();
    search.mutate(
      { files: picker.files, category, spec },
      {
        onError: (e) =>
          toast.error(e.response?.data.message ?? "이미지 검색에 실패했습니다."),
      },
    );
  };

  const goReceive = (item: ItemImageSearchResult) => {
    const { category, spec } = form.getValues();
    draft.setDraft({ files: picker.files, category, spec });
    router.push(`/stock-in?itemId=${item.itemId}`);
  };

  /** 사진 1장 업로드. 대표 사진이면 품목 imageUrl 도 갱신한다. */
  const uploadOne = async (itemId: string, imageId: string) => {
    const target = images.find((i) => i.id === imageId);
    if (!target) return false;
    picker.setStatus(imageId, "uploading");
    try {
      const saved = await upload.mutateAsync({ itemId, file: target.file });
      if (imageId === primaryId && saved) {
        await updateItem.mutateAsync({
          id: itemId,
          values: { imageUrl: saved.path },
        });
      }
      picker.setStatus(imageId, "done");
      return true;
    } catch (e) {
      const message = axios.isAxiosError<{ message?: string }>(e)
        ? e.response?.data?.message
        : undefined;
      picker.setStatus(imageId, "error", message ?? "업로드 실패");
      return false;
    }
  };

  const finish = (itemId: string) => {
    draft.clearDraft();
    router.push(`/items/${itemId}`);
  };

  const uploadAll = async (itemId: string) => {
    uploadingRef.current = true;
    // 대표 사진을 먼저 올려 정렬 순서 맨 앞에 오게 한다.
    const ordered = [...images]
      .filter((i) => i.status !== "done")
      .sort((a, b) => Number(b.id === primaryId) - Number(a.id === primaryId));
    let allOk = true;
    for (const image of ordered) {
      allOk = (await uploadOne(itemId, image.id)) && allOk;
    }
    uploadingRef.current = false;
    if (allOk) finish(itemId);
    else toast.error("일부 사진 업로드에 실패했습니다. 재시도하거나 건너뛸 수 있습니다.");
  };

  const retry = async (imageId: string) => {
    if (!createdId) return;
    uploadingRef.current = true;
    const ok = await uploadOne(createdId, imageId);
    uploadingRef.current = false;
    const othersDone = images.every((i) => i.id === imageId || i.status === "done");
    if (ok && othersDone) finish(createdId);
  };

  const onSubmit = async (values: ItemFormValues) => {
    let itemId = createdId;
    if (!itemId) {
      const res = await createItem.mutateAsync(toCreateItemRequest(values));
      itemId = res.data.data?.id ?? null;
      if (!itemId) return;
      setCreatedId(itemId);
    }
    await uploadAll(itemId);
  };

  const locked = createdId !== null;

  return (
    <div className='flex max-w-4xl flex-col gap-6'>
      <Card>
        <CardHeader>
          <CardTitle>1. 사진으로 기존 품목 찾기</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <ImagePicker
            images={images}
            primaryId={primaryId}
            disabled={isBusy || locked}
            onAdd={picker.addFiles}
            onRemove={picker.removeImage}
            onSelectPrimary={picker.setPrimaryId}
            onRetry={locked ? retry : undefined}
          />

          {step === "search" && (
            <>
              <div className='grid gap-4 sm:grid-cols-2'>
                <Field>
                  <FieldLabel htmlFor='search-category'>카테고리</FieldLabel>
                  <NativeSelect
                    id='search-category'
                    className='w-full'
                    {...form.register("category")}
                  >
                    <option value=''>전체</option>
                    {ITEM_CATEGORY_VALUES.map((c) => (
                      <option key={c} value={c}>
                        {ITEM_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field>
                  <FieldLabel htmlFor='search-spec'>
                    규격 (차이 비교용, 선택)
                  </FieldLabel>
                  <Input
                    id='search-spec'
                    placeholder='예: 폭 150cm / 면 100%'
                    {...form.register("spec")}
                  />
                </Field>
              </div>
              <div className='flex flex-wrap gap-2'>
                <Button
                  type='button'
                  disabled={images.length === 0 || search.isPending}
                  onClick={runSearch}
                >
                  {search.isPending ? "검색 중…" : "비슷한 품목 찾기"}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setStep("form")}
                >
                  검색 없이 새 품목 등록
                </Button>
              </div>
            </>
          )}

          {step === "search" && search.data && (
            <ItemRecommendations
              data={search.data}
              onSelectItem={goReceive}
              onCreateNew={() => setStep("form")}
            />
          )}
        </CardContent>
      </Card>

      {step === "form" && (
        <Card>
          <CardHeader>
            <CardTitle>2. 새 품목 등록</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='flex flex-col gap-4'
            >
              <ItemFormFields form={form} />
              <div className='flex flex-wrap gap-2'>
                <Button type='submit' disabled={isBusy}>
                  {isBusy
                    ? "저장 중…"
                    : locked
                      ? "사진 업로드 계속"
                      : "품목 등록"}
                </Button>
                {!locked && (
                  <Button
                    type='button'
                    variant='outline'
                    disabled={isBusy}
                    onClick={() => setStep("search")}
                  >
                    사진 검색으로 돌아가기
                  </Button>
                )}
                {locked && hasFailed && (
                  <Button
                    type='button'
                    variant='outline'
                    disabled={isBusy}
                    onClick={() => createdId && finish(createdId)}
                  >
                    건너뛰고 상세로 이동
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
